import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import useMatchMedia from '../../hooks/useMatchMedia';
import useVisualViewport from '../../hooks/useVisualViewport';
import { PHONE_NAV_MEDIA_QUERY } from '../Layout/phoneLayout';
import SearchResults, { buildGroups, flattenGroups, getOptionId } from './SearchResults';
import useGlobalSearch from './useGlobalSearch';
import './GlobalSearch.css';
import Icon from '../common/Icon';

const LIST_ID = 'global-search-results';
const MAX_QUERY_LENGTH = 100;

const GlobalSearch = () => {
    const history = useHistory();
    const { pathname } = useLocation();
    const isPhone = useMatchMedia(PHONE_NAV_MEDIA_QUERY);

    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);

    const inputRef = useRef(null);
    const overlayRef = useRef(null);

    const search = useGlobalSearch(query);
    const groups = useMemo(() => buildGroups(search.results), [search.results]);
    const items = useMemo(() => flattenGroups(groups), [groups]);

    const showOverlay = isPhone && isOpen;
    const showPanel = !isPhone && isOpen && query.trim().length > 0;
    useVisualViewport(overlayRef, showOverlay);

    const close = useCallback(() => {
        setIsOpen(false);
        inputRef.current?.blur();
    }, []);

    const openAndFocus = useCallback(() => {
        setIsOpen(true);
        inputRef.current?.focus();
        inputRef.current?.select();
    }, []);

    const handleSelect = useCallback((item) => {
        setIsOpen(false);
        setQuery('');
        inputRef.current?.blur();
        history.push(item.to);
    }, [history]);

    useEffect(() => {
        setActiveIndex(0);
    }, [items]);

    useEffect(() => {
        setIsOpen(false);
    }, [pathname]);

    // Код клавиши, а не символ: в русской раскладке Ctrl+K даёт "л"
    useEffect(() => {
        const handleShortcut = (event) => {
            if ((event.ctrlKey || event.metaKey) && (event.code === 'KeyK' || event.key === 'k')) {
                event.preventDefault();
                openAndFocus();
            }
        };
        window.addEventListener('keydown', handleShortcut);
        return () => window.removeEventListener('keydown', handleShortcut);
    }, [openAndFocus]);

    // Фокус ставится синхронно с нажатием, иначе iOS не откроет клавиатуру
    useLayoutEffect(() => {
        if (!showOverlay) return undefined;
        inputRef.current?.focus();
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [showOverlay]);

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            close();
            return;
        }
        if (!items.length) return;

        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setActiveIndex((index) => (index + 1) % items.length);
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex((index) => (index - 1 + items.length) % items.length);
        } else if (event.key === 'Enter' && search.status === 'ready' && items[activeIndex]) {
            event.preventDefault();
            handleSelect(items[activeIndex]);
        }
    };

    const isExpanded = showOverlay || showPanel;
    const activeItem = isExpanded ? items[activeIndex] : null;

    const input = (
        <input
            ref={inputRef}
            type="search"
            className="global-search-input"
            placeholder="Поиск"
            aria-label="Поиск по клиентам, автомобилям и заказам"
            role="combobox"
            aria-expanded={isExpanded}
            aria-controls={LIST_ID}
            aria-autocomplete="list"
            aria-activedescendant={activeItem ? getOptionId(LIST_ID, activeItem) : undefined}
            autoComplete="off"
            enterKeyHint="search"
            maxLength={MAX_QUERY_LENGTH}
            value={query}
            onChange={(event) => {
                setQuery(event.target.value);
                setIsOpen(true);
            }}
            onFocus={() => setIsOpen(true)}
            onBlur={isPhone ? undefined : () => setIsOpen(false)}
            onKeyDown={handleKeyDown}
        />
    );

    const results = (
        <SearchResults
            listId={LIST_ID}
            query={query}
            search={search}
            groups={groups}
            activeIndex={activeIndex}
            onHover={setActiveIndex}
            onSelect={handleSelect}
        />
    );

    if (isPhone) {
        return (
            <>
                <button
                    type="button"
                    className="global-search-icon"
                    aria-label="Открыть поиск"
                    onClick={() => setIsOpen(true)}
                >
                    <Icon name="search" size={20} />
                </button>
                {showOverlay && (
                    <div ref={overlayRef} className="global-search-overlay" role="dialog" aria-modal="true" aria-label="Поиск">
                        <div className="global-search-overlay-bar">
                            <button type="button" className="global-search-icon" aria-label="Закрыть поиск" onClick={close}>
                                <Icon name="back" size={20} />
                            </button>
                            {input}
                        </div>
                        <div className="global-search-overlay-body">{results}</div>
                    </div>
                )}
            </>
        );
    }

    return (
        <div className="global-search">
            <Icon name="search" size={16} className="global-search-field-icon" />
            {input}
            {!query && <kbd className="global-search-hint">Ctrl K</kbd>}
            {showPanel && (
                // Клик по результату не должен снимать фокус с поля раньше, чем сработает выбор
                <div className="global-search-panel" onMouseDown={(event) => event.preventDefault()}>
                    {results}
                </div>
            )}
        </div>
    );
};

export default GlobalSearch;
